/** Wait until the browser has had a paint opportunity; cancellation is idempotent. */
export function afterPaint(callback) {
  let cancelled = false;
  let frame = requestAnimationFrame(() => {
    if (cancelled) return;
    frame = requestAnimationFrame(() => {
      if (cancelled) return;
      cancelled = true;
      callback();
    });
  });
  return () => {
    cancelled = true;
    cancelAnimationFrame(frame);
  };
}

/**
 * Prefer a decoded frame submitted for composition. A stalled/unsupported
 * callback cannot hold presentation indefinitely; callers still check media
 * readiness before revealing it. This never copies frames or changes playback.
 */
export function afterVideoFrame(video, callback) {
  let done = false;
  let videoFrame = null;
  let timer = null;
  let cancelPaint = null;
  const cancel = () => {
    done = true;
    clearTimeout(timer);
    cancelPaint?.();
    if (videoFrame !== null) {
      try { video.cancelVideoFrameCallback?.(videoFrame); } catch (_) { /* already dispatched */ }
      videoFrame = null;
    }
  };
  const finish = () => {
    if (done) return;
    cancel();
    callback();
  };
  if (typeof video?.requestVideoFrameCallback === "function") {
    // Give the video callback a real opportunity on slower phones before
    // falling back to display ticks; racing it immediately can reveal early.
    timer = setTimeout(() => { cancelPaint = afterPaint(finish); }, 250);
    try { videoFrame = video.requestVideoFrameCallback(finish); }
    catch (_) { clearTimeout(timer); cancelPaint = afterPaint(finish); }
  } else {
    cancelPaint = afterPaint(finish);
  }
  return cancel;
}
